import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Settings, Search, AlertCircle, Printer, Mail, UserRound, List, LayoutGrid, Megaphone } from "lucide-react";
import { timeAgo, Bool, StatCell } from "@/components/roster/rosterUtils";
import { SetupCard } from "@/components/roster/SetupCard";

const openPrint = (posFilter, view) => {
  const params = new URLSearchParams();
  if (posFilter) params.set("position", posFilter);
  if (view === "depth") params.set("view", "depth");
  const qs = params.toString();
  window.open(`/roster/print${qs ? `?${qs}` : ""}`, "_blank", "noopener");
};

export function RosterActions({ canManage, hasSheetUrl, broadcastList, openBroadcast, data, posFilter, view, sync, syncing, setShowSettings }) {
  return (
          <div className="flex items-center gap-2">
            {canManage && hasSheetUrl && broadcastList.length > 0 && (
              <Button
                onClick={openBroadcast}
                data-testid="broadcast-btn"
                title={`One-tap email to all ${broadcastList.length} players + guardians`}
                className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 text-[11px] font-condensed uppercase tracking-widest"
              >
                <Megaphone className="w-3 h-3 mr-1" />
                Broadcast
                <span className="ml-2 text-[9px]">{broadcastList.length}</span>
              </Button>
            )}
            {hasSheetUrl && data?.players?.length > 0 && (
              <Button
                onClick={() => openPrint(posFilter, view)}
                data-testid="print-roster-btn"
                title={posFilter ? `Print ${posFilter} group` : "Print full roster"}
                className="rounded-none bg-surface-2 hover:bg-surface-3 text-white border border-surface-3 hover:border-amber h-10 text-[11px] font-condensed uppercase tracking-widest"
              >
                <Printer className="w-3 h-3 mr-1" />
                Print {view === "depth" ? "Depth Chart" : "Roster"}
              </Button>
            )}
            {canManage && hasSheetUrl && (
              <Button
                onClick={sync}
                disabled={syncing}
                data-testid="sync-roster-btn"
                className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 text-[11px] font-condensed uppercase tracking-widest"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing…" : "Sync From Sheet"}
              </Button>
            )}
            {canManage && (
              <Button
                onClick={() => setShowSettings(true)}
                data-testid="roster-settings-btn"
                className="rounded-none bg-surface-2 hover:bg-surface-3 text-white border border-surface-3 hover:border-amber h-10 text-[11px] font-condensed uppercase tracking-widest"
              >
                <Settings className="w-3 h-3 mr-1" />
                Roster Settings
              </Button>
            )}
          </div>
  );
}

export function RosterBanners({ hasSheetUrl, canManage, setShowSettings, data, staleForReminder, sync, syncing }) {
  return (
    <>
        {/* Empty state — no sheet configured */}
        {!hasSheetUrl && canManage && (
          <SetupCard onOpen={() => setShowSettings(true)} />
        )}
        {!hasSheetUrl && !canManage && (
          <div className="bg-surface border border-surface-3 p-8" data-testid="roster-empty-viewer">
            <div className="font-display text-2xl uppercase tracking-wider text-white mb-2">Roster not set up yet</div>
            <div className="text-sm text-slate-400">Your head coach or AD needs to connect the shared Google Sheet.</div>
          </div>
        )}

        {/* Sync error banner */}
        {data?.config?.last_sync_error && (
          <div className="flex items-start gap-3 border-l-2 border-red-500 bg-red-500/5 px-4 py-3" data-testid="sync-error-banner">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-red-300">Last sync failed</div>
              <div className="text-xs text-slate-400 mt-1">{data.config.last_sync_error}</div>
            </div>
          </div>
        )}

        {/* Daily reminder — data is stale */}
        {hasSheetUrl && staleForReminder && !data?.config?.last_sync_error && (
          <div className="flex items-start gap-3 border-l-2 border-amber bg-amber/5 px-4 py-3" data-testid="stale-banner">
            <AlertCircle className="w-4 h-4 text-amber shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber">Roster hasn't synced in over a day</div>
              <div className="text-xs text-slate-400 mt-1">
                {data?.config?.last_synced_at
                  ? `Last sync ${timeAgo(data.config.last_synced_at)}. Ask the AD if the sheet changed, then hit "Sync From Sheet".`
                  : "Give the sheet a first sync to pull the current roster into Gridiron OS."}
              </div>
            </div>
            {canManage && (
              <Button
                onClick={sync}
                disabled={syncing}
                data-testid="stale-sync-btn"
                className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-9 text-[10px] font-condensed uppercase tracking-widest"
              >
                Sync Now
              </Button>
            )}
          </div>
        )}
    </>
  );
}

export function RosterStats({ stats, data }) {
  return (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <StatCell label="Total Players" value={stats.total} />
            <StatCell label="AD Cleared" value={`${stats.ad} / ${stats.total || 0}`} tone="emerald" />
            <StatCell label="Coach OK to Play" value={`${stats.coach} / ${stats.total || 0}`} />
            <div className="border border-surface-3 bg-surface p-4" data-testid="last-synced-cell">
              <div className="text-[10px] font-condensed uppercase tracking-[0.28em] text-slate-500">Last Synced</div>
              <div className="font-display text-lg text-white mt-1">
                {data?.config?.last_synced_at ? timeAgo(data.config.last_synced_at) : "Never"}
              </div>
              {data?.config?.last_synced_by && (
                <div className="text-[10px] text-slate-500 mt-1 truncate">by {data.config.last_synced_by}</div>
              )}
            </div>
          </div>
  );
}

export function RosterFilters({ view, setView, q, setQ, posFilter, setPosFilter, positionsAll }) {
  return (
    <>
            <div className="flex items-center gap-1 border border-surface-3 w-fit" data-testid="view-toggle">
              <button
                onClick={() => setView("list")}
                data-testid="view-list"
                className={`px-3 py-2 text-[10px] font-condensed uppercase tracking-widest flex items-center gap-2 ${
                  view === "list" ? "bg-amber text-[#0B0D12]" : "text-slate-400 hover:text-white"
                }`}
              >
                <List className="w-3 h-3" /> List
              </button>
              <button
                onClick={() => setView("depth")}
                data-testid="view-depth"
                className={`px-3 py-2 text-[10px] font-condensed uppercase tracking-widest flex items-center gap-2 ${
                  view === "depth" ? "bg-amber text-[#0B0D12]" : "text-slate-400 hover:text-white"
                }`}
              >
                <LayoutGrid className="w-3 h-3" /> Depth Chart
              </button>
            </div>

            <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="roster-search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, email, guardian…"
                  className="pl-10 bg-surface border-surface-3 rounded-none h-10"
                />
              </div>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setPosFilter("")}
                  data-testid="pos-filter-all"
                  className={`text-[10px] font-condensed uppercase tracking-widest px-3 py-2 border ${
                    posFilter === "" ? "border-amber text-amber bg-amber/10" : "border-surface-3 text-slate-400 hover:border-slate-500"
                  }`}
                >All</button>
                {positionsAll.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPosFilter(p === posFilter ? "" : p)}
                    data-testid={`pos-filter-${p}`}
                    className={`text-[10px] font-condensed uppercase tracking-widest px-3 py-2 border ${
                      posFilter === p ? "border-amber text-amber bg-amber/10" : "border-surface-3 text-slate-400 hover:border-slate-500"
                    }`}
                  >{p}</button>
                ))}
              </div>
            </div>
    </>
  );
}

export function GroupEmailBar({ posFilter, emailLists, openMailto }) {
  return (
            <div className="flex flex-wrap items-center gap-2 -mt-2" data-testid="group-email-bar">
              <span className="text-[10px] font-condensed uppercase tracking-[0.28em] text-slate-500 mr-2">
                {posFilter ? `${posFilter} group:` : "Whole roster:"}
              </span>
              <Button
                onClick={() => openMailto(emailLists.guardians, "guardian")}
                disabled={emailLists.guardians.length === 0}
                data-testid="email-parents-btn"
                className="rounded-none bg-transparent hover:bg-surface text-white border border-surface-3 hover:border-amber h-8 text-[10px] font-condensed uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Mail className="w-3 h-3 mr-1 text-amber" />
                Email Parents
                <span className="ml-2 text-[9px] text-amber">{emailLists.guardians.length}</span>
              </Button>
              <Button
                onClick={() => openMailto(emailLists.players, "player")}
                disabled={emailLists.players.length === 0}
                data-testid="email-players-btn"
                className="rounded-none bg-transparent hover:bg-surface text-white border border-surface-3 hover:border-amber h-8 text-[10px] font-condensed uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <UserRound className="w-3 h-3 mr-1 text-amber" />
                Email Players
                <span className="ml-2 text-[9px] text-amber">{emailLists.players.length}</span>
              </Button>
              <span className="text-[10px] text-slate-500 italic">Opens your mail app with everyone BCC'd — addresses stay private.</span>
            </div>
  );
}

export function RosterTable({ filtered }) {
  return (
              <table className="w-full text-sm" data-testid="roster-table">
                <thead>
                  <tr className="text-[10px] font-condensed uppercase tracking-[0.25em] text-slate-500 border-b border-surface-3">
                    <th className="text-left px-4 py-3">Player</th>
                    <th className="text-left px-3 py-3">Pos</th>
                    <th className="text-center px-3 py-3">AD</th>
                    <th className="text-center px-3 py-3">Play?</th>
                    <th className="text-left px-3 py-3">Cell</th>
                    <th className="text-left px-3 py-3">Player Email</th>
                    <th className="text-left px-3 py-3">Guardian</th>
                    <th className="text-left px-3 py-3">Guardian Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const nameTone = p.ad_cleared
                      ? "text-emerald-400"
                      : "text-red-400";
                    return (
                      <tr key={p.id} className="border-b border-surface-3 last:border-b-0 hover:bg-surface-2" data-testid={`player-row-${p.id}`}>
                        <td className="px-4 py-3">
                          <div className={`font-semibold ${nameTone}`} data-testid={`player-name-${p.id}`}>
                            {p.last_name}, {p.first_name}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <span className="text-[10px] font-condensed uppercase tracking-widest border border-surface-3 px-2 py-1 text-slate-300">
                            {p.position || "—"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center"><div className="inline-flex"><Bool v={p.ad_cleared} /></div></td>
                        <td className="px-3 py-3 text-center"><div className="inline-flex"><Bool v={p.coach_cleared} /></div></td>
                        <td className="px-3 py-3 text-slate-300 whitespace-nowrap">{p.cell_number || "—"}</td>
                        <td className="px-3 py-3 text-slate-300 whitespace-nowrap">
                          {p.player_email ? (
                            <a href={`mailto:${p.player_email}`} className="hover:text-amber">{p.player_email}</a>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-3 text-slate-300 whitespace-nowrap">
                          {p.guardian_last_name || p.guardian_first_name
                            ? `${p.guardian_last_name || ""}${p.guardian_last_name && p.guardian_first_name ? ", " : ""}${p.guardian_first_name || ""}`
                            : "—"}
                        </td>
                        <td className="px-3 py-3 text-slate-300 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            {p.guardian_phone && <span>{p.guardian_phone}</span>}
                            {p.guardian_email && (
                              <a href={`mailto:${p.guardian_email}`} className="hover:text-amber text-xs">{p.guardian_email}</a>
                            )}
                            {!p.guardian_phone && !p.guardian_email && "—"}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
  );
}
